import {
	Braces,
	ChevronDown,
	ChevronRight,
	ClipboardPaste,
	GripVertical,
	Plus,
	Trash2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	asList,
	coerceDefault,
	type DeclaredParam,
	defaultIssue,
	hasOptions,
	jsonText,
	keyIssue,
	optionLabel,
	PARAM_TYPE_ICONS,
	PARAM_TYPES,
	type ParamOption,
	type ParamType,
	parseJsonText,
	parseOptionList,
	suggestKey,
	summariseParam,
	typeOf,
} from "./paramSchema";
import { initialValues, ParamField } from "./runParams";
import { btn, ConfirmDialog, input, NodeIcon } from "./ui";

let counter = 0;
const nextUid = () => {
	counter += 1;
	return `p${counter}`;
};

const syncIds = (ids: string[], length: number): string[] =>
	ids.length === length
		? ids
		: Array.from({ length }, (_, i) => ids[i] ?? nextUid());

const KEY_HINT = 'Other nodes read it as {{ params.<key> }}';

const field = `${input} text-[12px]`;
const tiny = "block text-[11px] font-medium text-slate-600";

const Issue = ({ children }: { children: string | null }) =>
	children ? (
		<p className="mt-0.5 text-[11px] text-danger">{children}</p>
	) : null;

const OptionsEditor = ({
	uid,
	options,
	onChange,
}: {
	uid: string;
	options: ParamOption[];
	onChange: (next: ParamOption[]) => void;
}) => {
	const [pasting, setPasting] = useState(false);
	const [pasted, setPasted] = useState("");
	const [dragFrom, setDragFrom] = useState<number | null>(null);
	const [rowIds, setRowIds] = useState<string[]>(() =>
		options.map(() => nextUid()),
	);

	const ids = syncIds(rowIds, options.length);
	if (ids !== rowIds) setRowIds(ids);

	const focusValue = (index: number) => {
		requestAnimationFrame(() => {
			const el = document.getElementById(`${uid}-opt-${index}`);
			if (el instanceof HTMLInputElement) {
				el.focus();
				el.select();
			}
		});
	};

	const insertAfter = (index: number) => {
		const next = [...options];
		const nextIds = [...ids];
		next.splice(index + 1, 0, { value: "" });
		nextIds.splice(index + 1, 0, nextUid());
		setRowIds(nextIds);
		onChange(next);
		focusValue(index + 1);
	};

	const removeAt = (index: number) => {
		setRowIds(ids.filter((_, i) => i !== index));
		onChange(options.filter((_, i) => i !== index));
		if (index > 0) focusValue(index - 1);
	};

	const move = (from: number, to: number) => {
		if (from === to) return;
		const next = [...options];
		const nextIds = [...ids];
		const [moved] = next.splice(from, 1);
		const [movedId] = nextIds.splice(from, 1);
		next.splice(to, 0, moved);
		nextIds.splice(to, 0, movedId);
		setRowIds(nextIds);
		onChange(next);
	};

	return (
		<div className="space-y-1.5">
			<div className="flex items-baseline justify-between gap-2">
				<span className={tiny}>Options</span>
				<button
					type="button"
					onClick={() => setPasting((on) => !on)}
					className="inline-flex items-center gap-1 text-[11px] font-medium text-primary"
				>
					<ClipboardPaste className="h-3 w-3" /> Paste list
				</button>
			</div>

			{pasting && (
				<div className="space-y-1.5 rounded-md border border-dashed border-slate-300 bg-slate-50 p-2">
					<label className="block space-y-1">
						<span className="block text-[11px] text-slate-500">
							One per line, as <span className="font-mono">value</span> or{" "}
							<span className="font-mono">value=Label</span>.
						</span>
						<textarea
							className={`${field} min-h-20 font-mono`}
							value={pasted}
							onChange={(event) => setPasted(event.target.value)}
						/>
					</label>
					<div className="flex justify-end gap-2">
						<button
							type="button"
							className={btn.ghost}
							onClick={() => {
								setPasted("");
								setPasting(false);
							}}
						>
							Cancel
						</button>
						<button
							type="button"
							className={btn.primary}
							disabled={parseOptionList(pasted).length === 0}
							onClick={() => {
								const seen = new Set(options.map((o) => o.value));
								const added = parseOptionList(pasted).filter(
									(o) => !seen.has(o.value),
								);
								onChange([
									...options.filter((o) => o.value.trim() !== ""),
									...added,
								]);
								setPasted("");
								setPasting(false);
							}}
						>
							Add {parseOptionList(pasted).length} rows
						</button>
					</div>
				</div>
			)}

			<div className="overflow-hidden rounded-md border border-slate-200">
				<div className="flex items-center gap-1 border-b border-slate-200 bg-slate-50 px-1.5 py-1 text-[10px] font-medium uppercase tracking-wide text-slate-400">
					<span className="w-4" />
					<span className="flex-1">Value</span>
					<span className="flex-1">Label</span>
					<span className="w-6" />
				</div>
				{options.length === 0 && (
					<p className="px-2 py-2 text-[11px] text-slate-400">
						No options yet — a select with none is an empty dropdown.
					</p>
				)}
				{options.map((option, index) => (
					<div
						key={ids[index]}
						className="flex items-center gap-1 border-b border-slate-100 px-1.5 py-1 last:border-0"
					>
						<button
							type="button"
							draggable
							aria-label={`Reorder option ${index + 1}`}
							onDragStart={() => setDragFrom(index)}
							onDragOver={(event) => event.preventDefault()}
							onDrop={() => {
								if (dragFrom !== null) move(dragFrom, index);
								setDragFrom(null);
							}}
							className="shrink-0 cursor-grab text-slate-300"
						>
							<GripVertical className="h-3.5 w-3.5" />
						</button>
						<input
							id={`${uid}-opt-${index}`}
							aria-label={`Option ${index + 1} value`}
							className={`${field} flex-1 font-mono`}
							value={option.value}
							onChange={(event) => {
								const next = [...options];
								next[index] = { ...option, value: event.target.value };
								onChange(next);
							}}
							onKeyDown={(event) => {
								if (event.key === "Enter") {
									event.preventDefault();
									insertAfter(index);
								}
								if (
									event.key === "Backspace" &&
									option.value === "" &&
									!option.label
								) {
									event.preventDefault();
									removeAt(index);
								}
							}}
						/>
						<input
							aria-label={`Option ${index + 1} label`}
							className={`${field} flex-1`}
							placeholder={option.value || "same as value"}
							value={option.label ?? ""}
							onChange={(event) => {
								const next = [...options];
								const label = event.target.value;
								next[index] =
									label === "" ? { value: option.value } : { ...option, label };
								onChange(next);
							}}
							onKeyDown={(event) => {
								if (event.key === "Enter") {
									event.preventDefault();
									insertAfter(index);
								}
							}}
						/>
						<button
							type="button"
							onClick={() => removeAt(index)}
							aria-label={`Remove option ${index + 1}`}
							className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-danger"
						>
							<Trash2 className="h-3.5 w-3.5" />
						</button>
					</div>
				))}
			</div>

			<button
				type="button"
				onClick={() => insertAfter(options.length - 1)}
				className="inline-flex items-center gap-1 text-[12px] font-medium text-primary"
			>
				<Plus className="h-3.5 w-3.5" /> Add option
			</button>
		</div>
	);
};

const DefaultControl = ({
	uid,
	param,
	onChange,
}: {
	uid: string;
	param: DeclaredParam;
	onChange: (next: unknown) => void;
}) => {
	const type = typeOf(param);
	const options = param.options ?? [];

	if (type === "boolean") {
		const on = param.default === true;
		return (
			<div className="space-y-1">
				<span className={tiny}>Default</span>
				<button
					type="button"
					role="switch"
					aria-checked={on}
					aria-label="Default value"
					onClick={() => onChange(!on)}
					className="flex items-center gap-2"
				>
					<span
						className={`relative h-5 w-9 rounded-full transition-colors ${
							on ? "bg-primary" : "bg-slate-300"
						}`}
					>
						<span
							className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${
								on ? "left-4.5" : "left-0.5"
							}`}
						/>
					</span>
					<span className="font-mono text-[12px] text-slate-600">
						{String(on)}
					</span>
				</button>
			</div>
		);
	}

	if (type === "number") {
		return (
			<label className="block space-y-1">
				<span className={tiny}>Default</span>
				<input
					type="number"
					className={field}
					value={typeof param.default === "number" ? param.default : ""}
					onChange={(event) =>
						onChange(
							event.target.value === ""
								? undefined
								: Number(event.target.value),
						)
					}
				/>
			</label>
		);
	}

	if (type === "select") {
		return (
			<label className="block space-y-1">
				<span className={tiny}>Default</span>
				<select
					className={field}
					value={param.default === undefined ? "" : String(param.default)}
					onChange={(event) => onChange(event.target.value || undefined)}
				>
					<option value="">— none —</option>
					{options.map((option) => (
						<option key={option.value} value={option.value}>
							{optionLabel(option)}
						</option>
					))}
				</select>
			</label>
		);
	}

	if (type === "multiselect") {
		const selected = new Set(asList(param.default));
		return (
			<div className="space-y-1">
				<span className={tiny}>Default</span>
				{options.length === 0 ? (
					<p className="text-[11px] text-slate-400">Add options first.</p>
				) : (
					<div className="flex flex-wrap gap-1.5">
						{options.map((option) => {
							const on = selected.has(option.value);
							return (
								<button
									key={option.value}
									type="button"
									aria-pressed={on}
									onClick={() => {
										const next = new Set(selected);
										if (on) next.delete(option.value);
										else next.add(option.value);
										const kept = options
											.map((o) => o.value)
											.filter((v) => next.has(v));
										onChange(kept.length > 0 ? kept : undefined);
									}}
									className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${
										on
											? "bg-primary text-white"
											: "bg-slate-100 text-slate-600 hover:bg-slate-200"
									}`}
								>
									{optionLabel(option)}
								</button>
							);
						})}
					</div>
				)}
			</div>
		);
	}

	return (
		<label className="block space-y-1">
			<span className={tiny}>Default</span>
			<input
				type="text"
				id={`${uid}-default`}
				className={field}
				value={typeof param.default === "string" ? param.default : ""}
				onChange={(event) => onChange(event.target.value || undefined)}
			/>
		</label>
	);
};

const JsonEscape = ({
	value,
	onChange,
}: {
	value: unknown;
	onChange: (next: unknown) => void;
}) => {
	const [open, setOpen] = useState(false);
	const [text, setText] = useState(() => jsonText(value));
	const [bad, setBad] = useState(false);
	const mine = useRef(jsonText(value));

	const canonical = jsonText(value);
	if (!bad && canonical !== mine.current) {
		mine.current = canonical;
		if (canonical !== text) setText(canonical);
	}

	if (!open)
		return (
			<button
				type="button"
				onClick={() => setOpen(true)}
				className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-400 hover:text-slate-600"
			>
				<Braces className="h-3 w-3" /> Edit as JSON
			</button>
		);

	return (
		<div className="space-y-1">
			<div className="flex items-baseline justify-between">
				<span className={tiny}>Default as JSON</span>
				<button
					type="button"
					onClick={() => setOpen(false)}
					className="text-[11px] font-medium text-slate-400 hover:text-slate-600"
				>
					Hide
				</button>
			</div>
			<input
				type="text"
				aria-label="Default as JSON"
				className={`${field} font-mono ${bad ? "border-danger" : ""}`}
				value={text}
				onChange={(event) => {
					const next = event.target.value;
					setText(next);
					const parsed = parseJsonText(next);
					if (parsed.ok) {
						setBad(false);
						mine.current = jsonText(parsed.value);
						onChange(parsed.value);
					} else {
						setBad(true);
					}
				}}
			/>
			<Issue>{bad ? "Not valid JSON yet" : null}</Issue>
		</div>
	);
};

const ParamCard = ({
	uid,
	param,
	index,
	open,
	autoFocus,
	keyError,
	defaultError,
	onToggle,
	onChange,
	onRemove,
	onDragStart,
	onDrop,
}: {
	uid: string;
	param: DeclaredParam;
	index: number;
	open: boolean;
	autoFocus: boolean;
	keyError: string | null;
	defaultError: string | null;
	onToggle: () => void;
	onChange: (next: DeclaredParam) => void;
	onRemove: () => void;
	onDragStart: () => void;
	onDrop: () => void;
}) => {
	const type = typeOf(param);
	const touched = useRef(param.key.trim() !== "");
	const headerRef = useRef<HTMLButtonElement>(null);
	const keyRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (autoFocus) keyRef.current?.focus();
	}, [autoFocus]);

	const set = (patch: Partial<DeclaredParam>) =>
		onChange({ ...param, ...patch });

	return (
		<fieldset
			onKeyDown={(event) => {
				if (event.key === "Escape" && open) {
					event.stopPropagation();
					onToggle();
					headerRef.current?.focus();
				}
			}}
			className={`min-w-0 rounded-lg border bg-canvas ${
				keyError || defaultError ? "border-danger/40" : "border-slate-200"
			}`}
		>
			<legend className="sr-only">
				{param.label || param.key || `Parameter ${index + 1}`}
			</legend>
			<div className="flex items-start gap-1.5 p-2">
				<button
					type="button"
					draggable
					aria-label={`Reorder ${param.key || `parameter ${index + 1}`}`}
					onDragStart={onDragStart}
					onDragOver={(event) => event.preventDefault()}
					onDrop={onDrop}
					className="mt-1 shrink-0 cursor-grab text-slate-300"
				>
					<GripVertical className="h-4 w-4" />
				</button>
				<button
					ref={headerRef}
					type="button"
					onClick={onToggle}
					aria-expanded={open}
					className="min-w-0 flex-1 text-left"
				>
					<span className="flex min-w-0 items-center gap-1.5">
						{open ? (
							<ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-400" />
						) : (
							<ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-400" />
						)}
						<span className="shrink truncate text-[12px] font-semibold text-slate-800">
							{param.label || param.key || "New parameter"}
						</span>
						{param.key.trim() !== "" && (
							<span className="shrink truncate font-mono text-[11px] text-slate-400">
								{param.key}
							</span>
						)}
						{param.default !== undefined && (
							<span
								title="Has a default"
								className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
							/>
						)}
					</span>
					<span className="mt-1 flex min-w-0 items-center gap-1.5">
						<span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
							<NodeIcon name={PARAM_TYPE_ICONS[type]} className="h-2.5 w-2.5" />
							{type}
						</span>
						{!open && (
							<span className="min-w-0 truncate text-[11px] text-slate-400">
								{summariseParam(param)}
							</span>
						)}
					</span>
				</button>
				<button
					type="button"
					onClick={onRemove}
					aria-label={`Delete ${param.key || `parameter ${index + 1}`}`}
					className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-danger"
				>
					<Trash2 className="h-3.5 w-3.5" />
				</button>
			</div>

			{open && (
				<div className="space-y-2.5 rounded-b-lg border-t border-slate-200 bg-white p-2.5">
					<label className="block space-y-1">
						<span className={tiny}>Label</span>
						<input
							className={field}
							value={param.label ?? ""}
							onChange={(event) => {
								const label = event.target.value;
								if (touched.current) {
									set({ label: label || undefined });
									return;
								}
								set({ label: label || undefined, key: suggestKey(label) });
							}}
						/>
					</label>

					<label className="block space-y-1">
						<span className={tiny}>
							Key <span className="text-danger">*</span>
						</span>
						<input
							ref={keyRef}
							id={`${uid}-key`}
							className={`${field} font-mono ${keyError ? "border-danger" : ""}`}
							value={param.key}
							onChange={(event) => {
								touched.current = true;
								set({ key: event.target.value });
							}}
						/>
						<Issue>{keyError}</Issue>
						<span className="block text-[10px] text-slate-400">{KEY_HINT}</span>
					</label>

					<label className="block space-y-1">
						<span className={tiny}>Type</span>
						<select
							className={field}
							value={type}
							onChange={(event) => {
								const next = event.target.value as ParamType;
								set({
									type: next,
									options: hasOptions(next) ? (param.options ?? []) : undefined,
									default: coerceDefault(next, param.default),
								});
							}}
						>
							{PARAM_TYPES.map((entry) => (
								<option key={entry} value={entry}>
									{entry}
								</option>
							))}
						</select>
					</label>

					{hasOptions(type) && (
						<OptionsEditor
							uid={uid}
							options={param.options ?? []}
							onChange={(options) => set({ options })}
						/>
					)}

					<div className="space-y-1">
						<DefaultControl
							uid={uid}
							param={param}
							onChange={(value) => set({ default: value })}
						/>
						<Issue>{defaultError}</Issue>
						<JsonEscape
							value={param.default}
							onChange={(value) => set({ default: value })}
						/>
					</div>

					<label className="block space-y-1">
						<span className={tiny}>Hint</span>
						<input
							className={field}
							value={param.hint ?? ""}
							onChange={(event) =>
								set({ hint: event.target.value || undefined })
							}
						/>
					</label>
				</div>
			)}
		</fieldset>
	);
};

const RunFormPreview = ({ params }: { params: DeclaredParam[] }) => {
	const [open, setOpen] = useState(false);
	const [overrides, setOverrides] = useState<Record<string, string>>({});
	const usable = useMemo(
		() => params.filter((param) => param.key.trim() !== ""),
		[params],
	);
	const defaults = useMemo(() => initialValues(usable), [usable]);

	return (
		<div className="rounded-lg border border-slate-200 bg-slate-50">
			<button
				type="button"
				onClick={() => setOpen((on) => !on)}
				aria-expanded={open}
				className="flex w-full items-center gap-1.5 px-2 py-1.5 text-left text-[11px] font-medium text-slate-500"
			>
				{open ? (
					<ChevronDown className="h-3.5 w-3.5" />
				) : (
					<ChevronRight className="h-3.5 w-3.5" />
				)}
				Preview as run form
			</button>
			{open && (
				<div className="space-y-3 border-t border-slate-200 bg-white p-2.5">
					{usable.length === 0 ? (
						<p className="text-[11px] text-slate-400">
							Nothing to show until a parameter has a key.
						</p>
					) : (
						usable.map((param) => (
							<ParamField
								key={param.key}
								param={param}
								value={overrides[param.key] ?? defaults[param.key] ?? ""}
								onChange={(next) =>
									setOverrides((current) => ({
										...current,
										[param.key]: next,
									}))
								}
							/>
						))
					)}
				</div>
			)}
		</div>
	);
};

export const TriggerParams = ({
	params,
	onChange,
}: {
	params: DeclaredParam[];
	onChange: (next: DeclaredParam[]) => void;
}) => {
	const [uids, setUids] = useState<string[]>(() => params.map(() => nextUid()));
	const [open, setOpen] = useState<Set<string>>(() => new Set());
	const [focusUid, setFocusUid] = useState<string | null>(null);
	const [pending, setPending] = useState<number | null>(null);
	const [dragFrom, setDragFrom] = useState<number | null>(null);
	const addRef = useRef<HTMLButtonElement>(null);

	const ids = syncIds(uids, params.length);
	if (ids !== uids) setUids(ids);

	const write = useCallback(
		(next: DeclaredParam[], nextIds: string[]) => {
			setUids(nextIds);
			onChange(next);
		},
		[onChange],
	);

	const toggle = (uid: string) =>
		setOpen((current) => {
			const next = new Set(current);
			if (next.has(uid)) next.delete(uid);
			else next.add(uid);
			return next;
		});

	const add = () => {
		const uid = nextUid();
		write([...params, { key: "", type: "string" }], [...ids, uid]);
		setOpen((current) => new Set(current).add(uid));
		setFocusUid(uid);
	};

	const move = (from: number, to: number) => {
		if (from === to) return;
		const nextParams = [...params];
		const nextIds = [...ids];
		const [movedParam] = nextParams.splice(from, 1);
		const [movedId] = nextIds.splice(from, 1);
		nextParams.splice(to, 0, movedParam);
		nextIds.splice(to, 0, movedId);
		write(nextParams, nextIds);
	};

	const remove = (index: number) => {
		write(
			params.filter((_, i) => i !== index),
			ids.filter((_, i) => i !== index),
		);
		setPending(null);
		addRef.current?.focus();
	};

	const doomed = pending === null ? null : params[pending];

	return (
		<div className="space-y-2">
			<div className="sticky top-0 z-10 -mx-3 flex items-baseline justify-between gap-2 border-b border-slate-200 bg-white px-3 py-1.5">
				<h4 className="text-[12px] font-semibold text-slate-700">
					Params · {params.length}
				</h4>
				<button
					ref={addRef}
					type="button"
					onClick={add}
					className="inline-flex items-center gap-1 text-[12px] font-medium text-primary"
				>
					<Plus className="h-3.5 w-3.5" /> Add param
				</button>
			</div>

			<RunFormPreview params={params} />

			{params.length === 0 ? (
				<p className="text-[12px] text-slate-400">
					No parameters yet. This flow runs with whatever each node is
					configured to do.
				</p>
			) : (
				<div className="space-y-2">
					{params.map((param, index) => (
						<ParamCard
							key={ids[index]}
							uid={ids[index]}
							param={param}
							index={index}
							open={open.has(ids[index])}
							autoFocus={focusUid === ids[index]}
							keyError={keyIssue(params, index)}
							defaultError={defaultIssue(param)}
							onToggle={() => toggle(ids[index])}
							onChange={(next) =>
								onChange(params.map((p, i) => (i === index ? next : p)))
							}
							onRemove={() => setPending(index)}
							onDragStart={() => setDragFrom(index)}
							onDrop={() => {
								if (dragFrom !== null) move(dragFrom, index);
								setDragFrom(null);
							}}
						/>
					))}
				</div>
			)}

			{doomed && (
				<ConfirmDialog
					title="Delete this parameter?"
					body={
						<p>
							<span className="font-mono">{doomed.key || "(no key)"}</span> is
							removed from the run dialog and from every schedule that sets it.
							Nodes reading it fall back to their own configuration.
						</p>
					}
					confirmLabel="Delete"
					danger
					onCancel={() => setPending(null)}
					onConfirm={() => remove(pending as number)}
				/>
			)}
		</div>
	);
};
